import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api } from "../api/client";
import type { CustomTheme, Family, User } from "../api/types";
import { customThemesCss } from "../lib/palette";
import { resolveTheme } from "../lib/themes";

interface AuthState {
  user: User | null;
  family: Family | null;
  /** True until the initial /me check settles, so we don't flash the login screen. */
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  isParent: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInChild: (
    familySlug: string,
    username: string,
    pin: string,
  ) => Promise<void>;
  register: (input: {
    familyName: string;
    parentName: string;
    email: string;
    password: string;
    registrationCode?: string;
    familySlug?: string;
  }) => Promise<void>;
  /** Joins an existing family as a second (or later) parent, via a
   * single-use invite code another parent generated. */
  joinFamily: (input: {
    code: string;
    parentName: string;
    email: string;
    password: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Folds a fresh user or family from a write response back into auth state.
   *
   * Appearance is read app-wide — the topbar, the greeting, the palette on
   * <html> — so the pickers hand their response up here rather than keeping a
   * private copy that the rest of the app would not see.
   */
  updateIdentity: (next: { user?: User; family?: Family }) => void;
  /** Drops local auth state after the server rejects a session mid-session. */
  handleUnauthorized: () => void;

  /**
   * The palettes this member has built. Held here rather than in the editor
   * because they are not only the editor's business: all of them are rendered
   * into the stylesheet below so the picker can preview each one, and the
   * active theme has to be checked against them to know it still exists.
   */
  customThemes: CustomTheme[];
  /** Re-reads the member's palettes after the editor changes one. */
  refreshCustomThemes: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    family: null,
    loading: true,
  });

  // The session is an httpOnly cookie, so the app cannot read it directly.
  // Asking the server who we are is the only way to restore state on load.
  useEffect(() => {
    let cancelled = false;

    api
      .me()
      .then(({ user, family }) => {
        if (!cancelled) setState({ user, family, loading: false });
      })
      .catch(() => {
        // A 401 here is the normal "not logged in yet" case, not an error.
        if (!cancelled) setState({ user: null, family: null, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user, family } = await api.login(email, password);
    setState({ user, family, loading: false });
  }, []);

  const signInChild = useCallback(
    async (familySlug: string, username: string, pin: string) => {
      const { user, family } = await api.loginChild(familySlug, username, pin);
      setState({ user, family, loading: false });
    },
    [],
  );

  const register = useCallback(
    async (input: {
      familyName: string;
      parentName: string;
      email: string;
      password: string;
      registrationCode?: string;
      familySlug?: string;
    }) => {
      const { user, family } = await api.registerFamily(input);
      setState({ user, family, loading: false });
    },
    [],
  );

  const joinFamily = useCallback(
    async (input: {
      code: string;
      parentName: string;
      email: string;
      password: string;
    }) => {
      const { user, family } = await api.joinFamily(input);
      setState({ user, family, loading: false });
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the call failed: the user asked to be logged out.
      setState({ user: null, family: null, loading: false });
    }
  }, []);

  const handleUnauthorized = useCallback(() => {
    setState({ user: null, family: null, loading: false });
  }, []);

  const updateIdentity = useCallback(
    (next: { user?: User; family?: Family }) => {
      setState((prev) => ({
        ...prev,
        user: next.user ?? prev.user,
        family: next.family ?? prev.family,
      }));
    },
    [],
  );

  const [customThemes, setCustomThemes] = useState<CustomTheme[]>([]);

  const refreshCustomThemes = useCallback(async () => {
    try {
      const { themes } = await api.listCustomThemes();
      setCustomThemes(themes);
    } catch {
      // A palette list that will not load is not worth blocking the app over:
      // the member simply sees the built-ins until the next attempt.
      setCustomThemes([]);
    }
  }, []);

  // Loaded per member, and dropped on sign-out — these are private, so a second
  // person logging in on the same device must not inherit the first one's list.
  const userId = state.user?.id;
  useEffect(() => {
    if (!userId) {
      setCustomThemes([]);
      return;
    }
    void refreshCustomThemes();
  }, [userId, refreshCustomThemes]);

  // Every custom palette this member owns, as CSS — not just the active one, so
  // the picker can paint each swatch in its own colours.
  //
  // A <style> element rather than inline custom properties on <html>: the dark
  // half of a palette is a `prefers-color-scheme` rule, and an inline style
  // cannot carry a media query. See lib/palette.ts.
  const css = useMemo(() => customThemesCss(customThemes), [customThemes]);
  useEffect(() => {
    const el = document.createElement("style");
    el.dataset.customThemes = "";
    el.textContent = css;
    document.head.append(el);
    return () => el.remove();
  }, [css]);

  // The palette is an attribute on <html> rather than a class on the app root,
  // so it also reaches what renders outside it: the scrollbar, the form
  // controls, and every fixed-position backdrop.
  const theme = resolveTheme(state.user?.theme, customThemes);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isParent: state.user?.role === "parent",
      signIn,
      signInChild,
      register,
      joinFamily,
      signOut,
      updateIdentity,
      handleUnauthorized,
      customThemes,
      refreshCustomThemes,
    }),
    [
      state,
      signIn,
      signInChild,
      register,
      joinFamily,
      signOut,
      updateIdentity,
      handleUnauthorized,
      customThemes,
      refreshCustomThemes,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
  return ctx;
}

/**
 * Turns an unknown thrown value into a message worth showing a user, and
 * signals the auth layer when a session has gone stale.
 */
export function useErrorHandler() {
  const { handleUnauthorized } = useAuth();

  return useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError) {
        if (err.isUnauthorized) handleUnauthorized();
        return err.message;
      }
      return "Something went wrong. Please try again.";
    },
    [handleUnauthorized],
  );
}
